import { z } from "zod";
import type { CRMUser, Tenant } from "../api/types";
import { isValidPhone, normalizePhone, passwordRules } from "./format";

export interface AuthResponse {
  token: string;
  user: CRMUser;
  tenant: Tenant | null;
}

export type OtpChannel = "email" | "phone";

export interface OtpRequestResponse {
  channel: OtpChannel;
  destination: string;
  expires_at: string;
  delivery?: string;
  message?: string;
  debug_code?: string;
  resend_available_in_seconds?: number;
}

export interface OtpVerifyResponse {
  channel: OtpChannel;
  destination: string;
  verified: boolean;
  verification_token: string;
}

export interface VerifiedOtp {
  destination: string;
  token: string;
}

export const loginSchema = z.object({
  email: z.string().trim().email("Enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

export type LoginValues = z.infer<typeof loginSchema>;

export const forgotPasswordSchema = z.object({
  email: z.string().trim().email("Enter a valid email address"),
});

export type ForgotPasswordValues = z.infer<typeof forgotPasswordSchema>;

export interface PasswordEmailResponse {
  message: string;
  delivery?: string;
  expires_at?: string;
  debug_url?: string;
}

export interface PasswordActionDetails {
  purpose: "invitation" | "password_reset";
  expires_at: string;
  user: {
    name?: string | null;
    email?: string | null;
  };
}

export interface PasswordActionCompleteResponse {
  status: "password_set";
  purpose: "invitation" | "password_reset";
  message: string;
}

export const resetPasswordSchema = z.object({
  new_password: z.string().refine((value) => passwordRules(value).every((rule) => rule.ok), {
    message: "Password must include uppercase, lowercase, number, special symbol, and be at least 8 characters",
  }),
  confirm_password: z.string().min(1, "Confirm your new password"),
}).superRefine((values, context) => {
  if (values.new_password !== values.confirm_password) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["confirm_password"],
      message: "Passwords do not match",
    });
  }
});

export type ResetPasswordValues = z.infer<typeof resetPasswordSchema>;

export const changePasswordSchema = z.object({
  current_password: z.string().min(1, "Current temporary password is required"),
  new_password: z.string().refine((value) => passwordRules(value).every((rule) => rule.ok), {
    message: "Password must include uppercase, lowercase, number, special symbol, and be at least 8 characters",
  }),
  confirm_password: z.string().min(1, "Confirm your new password"),
}).superRefine((values, context) => {
  if (values.new_password !== values.confirm_password) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["confirm_password"],
      message: "Passwords do not match",
    });
  }
  if (values.current_password && values.new_password === values.current_password) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["new_password"],
      message: "Choose a password different from the temporary password",
    });
  }
});

export type ChangePasswordValues = z.infer<typeof changePasswordSchema>;

export const signupSchema = z.object({
  name: z.string().trim().min(1, "Owner name is required"),
  business_name: z.string().trim().min(1, "Business name is required"),
  business_category: z.string().trim().min(1, "Business category is required"),
  preferred_lead_sources: z.string().trim().min(1, "Primary lead source is required"),
  country_code: z.enum(["IN", "AE"]),
  phone: z.string().trim().min(1, "Phone number is required"),
  email: z.string().trim().email("Enter a valid email address"),
  email_otp: z.string(),
  password: z.string().refine((value) => passwordRules(value).every((rule) => rule.ok), {
    message: "Password must include uppercase, lowercase, number, special symbol, and be at least 8 characters",
  }),
  terms_accepted: z.boolean().refine(Boolean, {
    message: "Accept the current Terms of Service",
  }),
  privacy_acknowledged: z.boolean().refine(Boolean, {
    message: "Acknowledge the current Privacy Policy",
  }),
  dpa_authority_accepted: z.boolean().refine(Boolean, {
    message: "Confirm your authority and accept the current DPA",
  }),
}).superRefine((values, context) => {
  if (!isValidPhone(normalizePhone(values.phone, values.country_code))) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ["phone"], message: "Enter a valid phone number for the selected country" });
  }
});

export type SignupValues = z.infer<typeof signupSchema>;

export const billingSignupSchema = z.object({
  name: z.string().trim().min(1, "Owner name is required"),
  business_name: z.string().trim().min(1, "Business name is required"),
  phone: z.string().trim().refine((value) => isValidPhone(normalizePhone(value)), {
    message: "Enter a valid phone number with country code, for example +91 98765 43210",
  }),
  email: z.string().trim().email("Enter a valid email address"),
  email_otp: z.string(),
  password: z.string().refine((value) => passwordRules(value).every((rule) => rule.ok), {
    message: "Password must include uppercase, lowercase, number, special symbol, and be at least 8 characters",
  }),
  terms_accepted: z.boolean().refine(Boolean, { message: "Accept the current Terms of Service" }),
  privacy_acknowledged: z.boolean().refine(Boolean, { message: "Acknowledge the current Privacy Policy" }),
  dpa_authority_accepted: z.boolean().refine(Boolean, { message: "Confirm your authority and accept the current DPA" }),
});

export type BillingSignupValues = z.infer<typeof billingSignupSchema>;

export interface LegalDocument {
  document_type: "terms" | "privacy" | "dpa";
  version: string;
  title: string;
  effective_at: string;
  published_url: string;
  content_hash_sha256: string;
  acceptance_action: "accepted" | "acknowledged";
  acceptance_text: string;
  requires_authority: boolean;
}

export interface CurrentLegalDocumentsResponse {
  documents: LegalDocument[];
  document_set_hash_sha256: string;
  approval_status: "approved" | "pending_legal_review";
}

export interface SubmittedLegalAcceptance {
  documents: Array<{
    document_type: LegalDocument["document_type"];
    version: string;
    content_hash_sha256: string;
    acceptance_action: LegalDocument["acceptance_action"];
    confirmed: true;
    authority_confirmed: boolean;
  }>;
}

export type SignupPayload = Omit<
  SignupValues,
  "terms_accepted" | "privacy_acknowledged" | "dpa_authority_accepted"
> & {
  email_verification_token: string;
  legal_acceptance: SubmittedLegalAcceptance;
};

export type BillingSignupPayload = Omit<
  BillingSignupValues,
  "email_otp" | "terms_accepted" | "privacy_acknowledged" | "dpa_authority_accepted"
> & {
  email_verification_token: string;
  legal_acceptance: SubmittedLegalAcceptance;
};
